/* eslint-disable no-console */
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import cx from "classix";
import { Container, LoginFeedback } from "../../components";
import { useAuthContext } from "../../context/auth";
import { login, verifyOTP } from "../../utils";
import { client } from "../../utils/supabase";
import "./stylesLogin.scss";

export const Login = () => {
  const [status, setStatus] = useState("form");
  const [step, setStep] = useState("email"); // "email" o "code"
  const [userEmail, setUserEmail] = useState("");
  const [mode, setMode] = useState("otp"); // "otp" o "password"
  const [expiresAt, setExpiresAt] = useState(null); // ISO del código vigente (para "válido hasta HH:MM")

  const { pais: contextPais } = useAuthContext();

  // Hora HH:MM del vencimiento del código, calculada del expires_at que devuelve la
  // edge function (nunca de un minuto hardcodeado en el front — la validez la fija la función).
  const formatHoraExpiracion = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleTimeString("es-CL", { hour: "2-digit", minute: "2-digit" });
    } catch {
      return "";
    }
  };

  const {
    formState: { errors },
    handleSubmit,
    register,
    reset,
  } = useForm();

  // Enviar email (paso 1)
  const onSubmitEmail = useCallback(
    async ({ email }) => {
      try {
        let pais = contextPais;

        if (!pais) {
          const hostname = window.location.hostname;
          if (hostname.includes("visiona.pe")) pais = "Perú";
          else if (hostname.includes("wecast.cl")) pais = "Chile";
          else if (hostname.includes("screenmedia.com.uy")) pais = "Uruguay";
          else if (hostname.includes("adigitalholding.com")) pais = "Region";
          else pais = "Chile";
        }

        const normalizedPais = /per/i.test(pais) ? "PER" : "CHI";
        const senderEmail = normalizedPais === "PER" ? "hola@visiona.pe" : "hola@wecast.cl";

        setStatus("sending_code");

        const result = await login({
          email: email.trim(),
          pais: normalizedPais,
          senderEmail,
        });

        const parsedResult = typeof result === "string" ? JSON.parse(result) : result;

        if (parsedResult.success) {
          setUserEmail(email.trim());
          setStep("code");
          setExpiresAt(parsedResult.expires_at || null);
          // ya_enviado = ya había un código vigente; no se reenvió (cooldown en la edge function)
          setStatus(parsedResult.ya_enviado ? "code_already_sent" : "code_sent");
          reset(); // Limpiar el formulario
        } else {
          console.log("Error backend:", parsedResult.error);
          if (parsedResult.error === "invalid_email") {
            setStatus("invalid_email");
          } else {
            setStatus("error");
          }
        }
      } catch (error) {
        console.error("EXCEPTION en onSubmitEmail:", error);
        setStatus("error");
      }
    },
    [contextPais, reset]
  );

  // Verificar código (paso 2)
  // Verificar código (paso 2)
  const onSubmitCode = useCallback(
    async ({ code }) => {
      try {
        let pais = contextPais;

        if (!pais) {
          const hostname = window.location.hostname;
          if (hostname.includes("visiona.pe")) pais = "Perú";
          else if (hostname.includes("wecast.cl")) pais = "Chile";
          else if (hostname.includes("screenmedia.com.uy")) pais = "Uruguay";
          else if (hostname.includes("adigitalholding.com")) pais = "Region";
          else pais = "Chile";
        }

        const normalizedPais = /per/i.test(pais) ? "PER" : "CHI";

        setStatus("verifying_code");

        const result = await verifyOTP({
          email: userEmail,
          code: code.trim(),
          pais: normalizedPais,
        });

        const parsedResult = typeof result === "string" ? JSON.parse(result) : result;

        if (parsedResult.success && parsedResult.token) {
          setStatus("success");

          console.log("Verificando token:", parsedResult.token);

          // Usar verifyOtp para establecer la sesión persistente
          const { data, error } = await client.auth.verifyOtp({
            token_hash: parsedResult.token,
            type: "magiclink",
          });

          if (error) {
            console.error("Error verificando token:", error);
            setStatus("error");
            return;
          }

          console.log("Sesión establecida:", data);

          // Verificar que la sesión persiste
          const { data: sessionCheck } = await client.auth.getSession();
          console.log("Verificación de sesión:", sessionCheck);

          if (sessionCheck?.session) {
            // Redirigir al admin
            window.location.href = "/admin";
          } else {
            console.error("La sesión no se persistió correctamente");
            setStatus("error");
          }
        } else {
          console.log("Código inválido:", parsedResult.error);
          setStatus("invalid_code");
        }
      } catch (error) {
        console.error("EXCEPTION en onSubmitCode:", error);
        setStatus("error");
      }
    },
    [userEmail, contextPais]
  );

  // Volver al paso de email
  const handleBackToEmail = () => {
    setStep("email");
    setStatus("form");
    setUserEmail("");
    setExpiresAt(null);
    reset();
  };

  // Login con contraseña (modo alternativo)
  const onSubmitPassword = useCallback(async ({ email, password }) => {
    try {
      setStatus("verifying_password");

      const { data, error } = await client.auth.signInWithPassword({
        email: email.trim(),
        password,
      });

      if (error) {
        console.error("Error login con contraseña:", error);
        setStatus("invalid_password");
        return;
      }

      if (data?.session) {
        setStatus("success");
        window.location.href = "/admin";
      } else {
        setStatus("error");
      }
    } catch (error) {
      console.error("EXCEPTION en onSubmitPassword:", error);
      setStatus("error");
    }
  }, []);

  // Alternar entre modos OTP y password
  const handleSwitchMode = () => {
    setMode(mode === "otp" ? "password" : "otp");
    setStep("email");
    setStatus("form");
    setUserEmail("");
    setExpiresAt(null);
    reset();
  };

  // Si llegamos acá porque la sesión venció (la marca la pone fetchDataAuthenticated al no poder
  // refrescar el token, en supabase.js), avisamos claro en vez de dejar al usuario sin contexto.
  useEffect(() => {
    try {
      if (sessionStorage.getItem("adigital_sesion_expirada")) {
        sessionStorage.removeItem("adigital_sesion_expirada");
        toast.error("Tu sesión expiró. Iniciá sesión de nuevo para continuar.");
      }
    } catch {
      /* storage bloqueado: sin aviso, el login funciona igual */
    }
  }, []);

  useEffect(() => {
    if (
      status === "invalid_email" ||
      status === "invalid_code" ||
      status === "invalid_password" ||
      status === "error"
    ) {
      const timer = setTimeout(() => {
        setStatus(step === "email" || mode === "password" ? "form" : "code_sent");
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [status, step, mode]);

  return (
    <Container className="Login" header titulo="INICIAR SESIÓN">
      {/* ─── MODO OTP (flujo actual) ─── */}
      {mode === "otp" && step === "email" && (
        <form className="row g-3 justify-content-md-center" onSubmit={handleSubmit(onSubmitEmail)}>
          <div className="col-6">
            <input
              className={cx("form-control", errors?.email && "is-invalid")}
              id="email"
              placeholder="Ingrese su correo"
              type="email"
              autoComplete="email"
              {...register("email", { required: true })}
            />
            {status === "invalid_email" && (
              <div className="invalid-feedback" style={{ display: "block" }}>
                El correo ingresado no está autorizado
              </div>
            )}
          </div>

          <div className="col-12 text-center">
            <button className="botonLogin" type="submit">
              ENVIAR CÓDIGO
            </button>
          </div>
        </form>
      )}

      {mode === "otp" && step === "code" && (
        <form className="row g-3 justify-content-md-center" onSubmit={handleSubmit(onSubmitCode)}>
          <div className="col-12 text-center" style={{ marginBottom: "1rem" }}>
            <p style={{ color: "#666", fontSize: "0.95rem" }}>
              Ingresa el código de 6 dígitos enviado a:
              <br />
              <strong>{userEmail}</strong>
            </p>
          </div>
          <div className="col-6">
            <input
              className={cx("form-control", errors?.code && "is-invalid")}
              id="code"
              placeholder="------"
              type="text"
              maxLength="6"
              autoComplete="off"
              style={{ textAlign: "center", fontSize: "1.5rem", letterSpacing: "0.5rem" }}
              {...register("code", {
                required: true,
                pattern: /^\d{6}$/,
              })}
            />
            {status === "invalid_code" && (
              <div className="invalid-feedback" style={{ display: "block" }}>
                Código inválido o expirado
              </div>
            )}
          </div>

          <div className="col-12 text-center">
            <button className="botonLogin" type="submit">
              VERIFICAR
            </button>
            <button
              type="button"
              className="btn btn-link"
              onClick={handleBackToEmail}
              style={{ display: "block", margin: "1rem auto 0", textDecoration: "none" }}
            >
              Usar otro correo
            </button>
          </div>
        </form>
      )}

      {/* ─── MODO PASSWORD (fallback) ─── */}
      {mode === "password" && (
        <form className="row g-3 justify-content-md-center" onSubmit={handleSubmit(onSubmitPassword)}>
          <div className="col-6">
            <input
              className={cx("form-control", errors?.email && "is-invalid")}
              id="email"
              placeholder="Ingrese su correo"
              type="email"
              autoComplete="email"
              {...register("email", { required: true })}
            />
          </div>
          <div className="col-6">
            <input
              className={cx("form-control", errors?.password && "is-invalid")}
              id="password"
              placeholder="Contraseña"
              type="password"
              autoComplete="current-password"
              {...register("password", { required: true })}
            />
            {status === "invalid_password" && (
              <div className="invalid-feedback" style={{ display: "block" }}>
                Correo o contraseña incorrectos
              </div>
            )}
          </div>

          <div className="col-12 text-center">
            <button className="botonLogin" type="submit">
              INGRESAR
            </button>
          </div>
        </form>
      )}

      {/* ─── Feedback de estado ─── */}
      <div className="col-12 text-center" style={{ marginTop: "2rem", minHeight: "100px", display: "block" }}>
        {status === "sending_code" && <div className="loading-message">Enviando código...</div>}
        {status === "code_sent" && (
          <div className="success-message" style={{ color: "green", fontSize: "1.1rem", display: "block" }}>
            Código enviado. Revisa tu mail por favor.
          </div>
        )}
        {status === "code_already_sent" && (
          <div className="success-message" style={{ color: "green", fontSize: "1.1rem", display: "block" }}>
            Ya te enviamos un código, revisá tu correo
            {expiresAt ? ` (válido hasta las ${formatHoraExpiracion(expiresAt)})` : ""}.
          </div>
        )}
        {status === "verifying_code" && <div className="loading-message">Verificando código...</div>}
        {status === "verifying_password" && <div className="loading-message">Verificando...</div>}
        {status === "success" && (
          <div className="success-message" style={{ color: "green", fontSize: "1.1rem", display: "block" }}>
            ¡Acceso correcto! Redirigiendo...
          </div>
        )}
        {status === "error" && (
          <div className="invalid-feedback" style={{ display: "block", marginTop: "2rem", fontSize: "1.1rem" }}>
            Ocurrió un error. Por favor, intente nuevamente.
          </div>
        )}
        {status !== "form" &&
          status !== "sending_code" &&
          status !== "code_sent" &&
          status !== "code_already_sent" &&
          status !== "verifying_code" &&
          status !== "verifying_password" &&
          status !== "success" &&
          status !== "error" &&
          status !== "invalid_email" &&
          status !== "invalid_code" &&
          status !== "invalid_password" && <LoginFeedback status={status} />}
      </div>

      {/* ─── Link para alternar modo ─── */}
      <div className="col-12 text-center" style={{ marginTop: "1rem" }}>
        <button
          type="button"
          className="btn btn-link"
          onClick={handleSwitchMode}
          style={{ textDecoration: "none", color: "#888", fontSize: "0.9rem" }}
        >
          {mode === "otp" ? "¿No te llega el código? Ingresá con contraseña" : "Volver a ingreso con código"}
        </button>
      </div>
    </Container>
  );
};
